import { ChangePasswordDialog } from "@/routes/(app)/_main-layout/_settings/-components/change-password-dialog";
import { InfoModal } from "@/routes/(app)/_main-layout/_settings/-components/info-modal";
import { VolumeApi } from "@/lib/volume-api";
import { createFileRoute } from "@tanstack/react-router";
import { ChangePhoneNumberDialog } from "@/routes/(app)/_main-layout/_settings/-components/change-phone-number-dialog";
import { useMerchant } from "@/hooks/use-merchant";
import { CardWithHeader } from "@/components/card-with-header";
import { Separator } from "@/components/ui/separator";

export const Route = createFileRoute(
  "/(app)/_main-layout/_settings/settings/account-settings",
)({
  component: () => <MerchantProfileComponent />,
  loader: async ({ context }) => {
    const api = new VolumeApi(
      context.accessToken,
      context.selectedApplicationId,
    );
    const isVolumeAdmin =
      context.authorities.includes("VOLUME_ADMIN") ||
      context.authorities.includes("VOLUME_ADMIN_READ");
    if (isVolumeAdmin) {
      return null;
    }
    const accountInformation = await api.getAccountInformationSelf();
    const roles = await api.getMerchantRoles({
      merchantId: context.merchantId,
    });
    return {
      accountInformation,
      roles,
    };
  },
});

const MerchantProfileComponent = () => {
  const context = Route.useRouteContext();
  const loaderData = Route.useLoaderData();
  const merchant = useMerchant();

  return (
    <CardWithHeader variant="exchange" title="Account Settings">
      <div className="space-y-4">
        {loaderData?.accountInformation ? (
          <div className="grid grid-cols-12 gap-4 text-sm">
            <div className="col-span-2 text-black">Full Name</div>
            <div className="col-span-8 text-volume-dark-grey">
              {loaderData.accountInformation.name}
            </div>
          </div>
        ) : null}

        <Separator />

        <div className="grid grid-cols-12 gap-4 text-sm">
          <div className="col-span-2 text-black flex items-center">Email</div>
          <div className="col-span-8 text-volume-dark-grey flex items-center">
            <span>{context.isAdmin ? "Viewing as admin" : context.email}</span>
          </div>
          <div className="col-span-2 flex justify-end">
            {!context.isAdmin && <ChangePasswordDialog />}
          </div>
        </div>

        {loaderData?.accountInformation?.phone ? (
          <>
            <Separator />
            <div className="grid grid-cols-12 gap-4 text-sm">
              <div className="col-span-2 text-black flex items-center">
                Phone Number
              </div>
              <div className="col-span-8 text-volume-dark-grey flex items-center">
                <span>{loaderData.accountInformation.phone}</span>
              </div>
              <div className="col-span-2">
                <ChangePhoneNumberDialog
                  currentPhoneNumber={loaderData.accountInformation.phone}
                />
              </div>
            </div>
          </>
        ) : null}

        <Separator />

        <div className="grid grid-cols-12 gap-4 text-sm">
          <div className="col-span-2 text-black">Company name</div>
          <div className="col-span-8 text-volume-dark-grey">
            {merchant.companyName}
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-12 gap-4 text-sm">
          <div className="col-span-2 text-black">Merchant ID</div>
          <div className="col-span-8 text-volume-dark-grey">
            {context.merchantId}
          </div>
        </div>

        {loaderData?.accountInformation ? (
          <>
            <Separator />
            <div className="grid grid-cols-12 gap-4 text-sm">
              <div className="col-span-2 text-black flex items-center gap-2">
                Permissions
                <InfoModal roles={loaderData.roles} />
              </div>
              <div className="col-span-8 text-volume-dark-grey flex flex-wrap items-center">
                {loaderData.accountInformation?.permissions.map((role) => (
                  <div
                    key={role.id}
                    className="text-center rounded-2xl px-3 py-[2px] inline-block bg-volume-mid-purple text-black mr-2 mb-2"
                  >
                    {role.name}
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </CardWithHeader>
  );
};
